import { LOGGER_CONFIG } from "../config/logger.config";
import { API_BASE_URL, API_VERSION } from "../services/api";

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

class Logger {
  constructor() {
    this.currentLevel =
      process.env.NODE_ENV === "production"
        ? LOG_LEVELS.WARN
        : LOG_LEVELS.DEBUG;

    this.errorBuffer = [];
    this.maxBufferSize = LOGGER_CONFIG.maxBufferSize; // Max errors to store before forcing a flush
    this.flushInterval = LOGGER_CONFIG.flushInterval;
    this.flushTimer = null;
    this.isFlushing = false;

    // Start the periodic flush timer
    this.startFlushTimer();
  }

  /**
   * Debug logging - only in development
   */
  debug(message, ...args) {
    if (this.currentLevel <= LOG_LEVELS.DEBUG) {
      console.log(`[DEBUG]`, message, ...args);
    }
  }

  /**
   * Info logging - only in development
   */
  info(message, ...args) {
    if (this.currentLevel <= LOG_LEVELS.INFO) {
      console.info(`[INFO]`, message, ...args);
    }
  }

  /**
   * Warning logging - development and production
   */
  warn(message, ...args) {
    if (this.currentLevel <= LOG_LEVELS.WARN) {
      console.warn(`[WARN]`, message, ...args);
    }

    if (process.env.NODE_ENV === "production") {
      this.bufferLog({
        level: "warn",
        message,
        data: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    }
  }

  /**
   * Error logging - always logged
   */
  error(message, error, ...args) {
    if (this.currentLevel <= LOG_LEVELS.ERROR) {
      console.error(`[ERROR]`, message, error, ...args);
    }

    this.bufferLog({
      level: "error",
      message,
      error: this.serializeError(error),
      data: this.sanitizeArgs(args),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      stackTrace: error?.stack,
    });
  }

  /**
   * Critical error logging - always logged and flushed immediately
   * Used for unrecoverable errors
   */
  critical(message, error, ...args) {
    // Critical errors - flush immediately
    this.error(message, error, ...args);
    this.flushImmediate();
  }

  /**
   * Sanitize arguments to remove sensitive data and handle circular references
   */
  sanitizeArgs(args) {
    try {
      return args.map((arg) => {
        if (arg === null || arg === undefined) return arg;

        // Handle objects
        if (typeof arg === "object") {
          // Create a shallow copy to avoid circular reference issues
          const sanitized = {};
          for (const key in arg) {
            // Skip sensitive fields
            if (LOGGER_CONFIG.sensitiveFields.includes(key)) {
              sanitized[key] = "[REDACTED]";
            } else {
              try {
                // Try to stringify to check for circular references
                JSON.stringify(arg[key]);
                sanitized[key] = arg[key];
              } catch (e) {
                sanitized[key] = "[Circular]";
              }
            }
          }
          return sanitized;
        }

        return arg;
      });
    } catch (e) {
      return ["[Sanitization Error]"];
    }
  }

  /**
   * Serialize error objects for logging
   */
  serializeError(error) {
    if (!error) return null;

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    // For non-Error objects (like axios errors)
    if (error.response) {
      return {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        message: error.message,
      };
    }

    return error;
  }

  /**
   * Buffer logs to send to backend in batches
   */
  bufferLog(logData) {
    this.errorBuffer.push(logData);

    // Force flush if buffer is getting too large
    if (this.errorBuffer.length >= this.maxBufferSize) {
      this.flushLogs();
    }
  }

  /**
   * Start periodic flush timer
   */
  startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    // Only run timer in production
    if (process.env.NODE_ENV === "production") {
      this.flushTimer = setInterval(() => {
        this.flushLogs();
      }, this.flushInterval);
    }
  }

  /**
   * Stop the flush timer
   */
  stopFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Send buffered logs to backend
   */
  async flushLogs() {
    // Nothing to send
    if (this.errorBuffer.length === 0) return;

    // Already flushing
    if (this.isFlushing) return;

    this.isFlushing = true;
    const logsToSend = [...this.errorBuffer];
    this.errorBuffer = []; // Clear buffer immediately

    try {
      await fetch(`${API_BASE_URL}${API_VERSION}/logs/frontend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          logs: logsToSend,
          context: {
            userAgent: navigator.userAgent,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV,
          },
        }),
        // Don't wait forever
        signal: AbortSignal.timeout(LOGGER_CONFIG.requestTimeout),
      });

      this.debug(`Successfully sent ${logsToSend.length} logs to backend`);
    } catch (err) {
      // Failed to send logs - put them back in buffer (at the front)
      console.error("Failed to send logs to backend:", err);
      this.errorBuffer.unshift(...logsToSend);

      // Trim buffer if it's getting too large
      if (this.errorBuffer.length > this.maxBufferSize * 2) {
        this.errorBuffer = this.errorBuffer.slice(0, this.maxBufferSize);
        console.warn("Log buffer overflow - dropping oldest logs");
      }
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Flush logs immediately (useful for critical errors)
   */
  flushImmediate() {
    return this.flushLogs();
  }

  /**
   * Setup listeners for page unload and visibility changes
   */
  setupEventListeners() {
    // Flush when page is about to unload
    window.addEventListener("beforeunload", () => {
      if (this.errorBuffer.length > 0) {
        // Use sendBeacon for reliable delivery on page unload
        // sendBeacon is synchronous and doesn't block page unload
        const blob = new Blob(
          [
            JSON.stringify({
              logs: this.errorBuffer,
              context: {
                userAgent: navigator.userAgent,
                viewport: {
                  width: window.innerWidth,
                  height: window.innerHeight,
                },
                timestamp: new Date().toISOString(),
                environment: process.env.NODE_ENV,
              },
            }),
          ],
          { type: "application/json" },
        );

        navigator.sendBeacon(
          `${API_BASE_URL}${API_VERSION}/logs/frontend`,
          blob,
        );
      }
    });

    // Flush when page becomes hidden (tab switch, minimize, etc.)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.errorBuffer.length > 0) {
        this.flushLogs();
      }
    });

    // Listen for global unhandled errors
    window.addEventListener("error", (event) => {
      this.error("Unhandled error:", event.error, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Listen for unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      this.error("Unhandled promise rejection:", event.reason);
    });
  }

  /**
   * Cleanup - call this when app is destroyed (rare in SPAs)
   */
  destroy() {
    this.stopFlushTimer();
    this.flushLogs(); // Final flush
  }
}

// Create singleton instance
const logger = new Logger();

// Setup event listeners
if (typeof window !== "undefined") {
  logger.setupEventListeners();
}

export default logger;
